import { clearTokens, queryClient, storage, useTokens } from '@frontend/shared';
import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';

import { decodeJwt, splitFullName, type AllocabJwtClaims } from '../lib/jwt';
import { useAccountConfig } from '../services/api/hooks/useAccountConfig';
import { useMe } from '../services/api/hooks/useMe';
import { useUserPermissions } from '../services/api/hooks/useUserPermissions';
import { useUserRolesPermissions } from '../services/api/hooks/useUserRolesPermissions';
import type {
  AccountConfig,
  ExtranetMeResponse,
  ModuleKey,
  UserPermissionsResponse,
} from '../types/extranet-api.types';

interface AccountContextValue {
  me: ExtranetMeResponse;
  config: AccountConfig;
  permissions: UserPermissionsResponse;
  /** Helper : true si l'utilisateur a le flag X sur le module Y */
  can: <M extends ModuleKey>(module: M, flag: keyof UserPermissionsResponse['modules'][M]) => boolean;
}

const AccountContext = createContext<AccountContextValue | null>(null);

interface AccountProviderProps {
  children: ReactNode;
  error: ReactNode;
}

const getHttpStatus = (err: unknown): number | undefined => {
  if (err && typeof err === 'object') {
    const e = err as { statusCode?: number; response?: { status?: number }; status?: number };
    return e.statusCode ?? e.response?.status ?? e.status;
  }
  return undefined;
};

const isAuthError = (err: unknown): boolean => {
  const status = getHttpStatus(err);
  return status === 401 || status === 403;
};

/**
 * Builders qui produisent `me` / `config` / `permissions` depuis les claims
 * du JWT Allocab. Utilisés tant que Yohan n'a pas livré les routes back
 * correspondantes ; dès qu'elles répondent 200, le provider merge avec les
 * valeurs officielles (back wins).
 *
 * Pour le module Admin : on appelle `GET /v1/users/:userId/permissions` (route
 * livrée le 2026-06-30) avec `claims.id` au boot ; si la réponse contient
 * `EXTRANET_ADMIN` dans `permissions[]`, on active `admin.{read,manageUsers}`.
 */

const buildMe = (claims: AllocabJwtClaims): ExtranetMeResponse => {
  const { firstName, lastName } = splitFullName(claims.userFullName);
  return {
    id: claims.id,
    email: '',
    firstName,
    lastName,
    accountId: claims.accountKey,
    accountName: claims.accountCategory ? `Compte ${claims.accountCategory}` : claims.accountKey,
    roles: claims.roles,
    createdAt: new Date(claims.issued).toISOString(),
  };
};

/**
 * Permissions par défaut (avant arrivée de la réponse back). Les flags
 * d'écriture restent à `false` :
 * - `incidents.create/update` ← POST /v1/extranet/incidents non livré
 * - `admin.{read,manageUsers}` ← activés UNIQUEMENT si EXTRANET_ADMIN remonté
 * - `lostFound.*` ← back lost & found pas livré (module masqué)
 *
 * Les flags de lecture / téléchargement sont à `true` puisque les routes
 * correspondantes répondent 200 en PROD.
 */
const buildPermissions = (claims: AllocabJwtClaims): UserPermissionsResponse => ({
  userId: claims.id,
  modules: {
    dashboard: { read: true },
    incidents: { read: true, create: false, update: false },
    performance: { read: true },
    invoices: { read: true, download: true },
    lostFound: { read: false, update: false },
    admin: { read: false, manageUsers: false },
  },
});

const buildConfig = (claims: AllocabJwtClaims): AccountConfig => ({
  accountId: claims.accountKey,
  name: claims.accountCategory ? `Compte ${claims.accountCategory}` : claims.accountKey,
  logoUrl: null,
  labels: {
    account: 'Compte',
    entity: 'Entité',
    contract: 'Contrat',
    site: 'Établissement',
    agent: 'Agent',
    ride: 'Course',
    externalRef: 'Référence externe',
  },
  incidentCategories: [],
  kpiThresholds: {
    coverage: { success: 98, warning: 95 },
    punctuality: { success: 95, warning: 90 },
    incident: { success: 2, warning: 5 },
    security: { success: 0.5, warning: 1 },
    service: { success: 99, warning: 97 },
    late_cancellation: { success: 2, warning: 5 },
    no_show: { success: 1, warning: 3 },
  },
  vatRate: 20,
  passwordPolicy: {
    minLength: 12,
    requireUppercase: true,
    requireDigit: true,
    requireSpecial: true,
    expirationDays: 90,
    maxFailedAttempts: 5,
  },
});

export const AccountProvider = ({ children, error }: AccountProviderProps) => {
  const { tokens } = useTokens();
  const claims = useMemo(() => decodeJwt(tokens?.accessToken), [tokens?.accessToken]);

  const meQ = useMe();
  const configQ = useAccountConfig();
  const permsQ = useUserPermissions();
  const rolesPermsQ = useUserRolesPermissions(claims?.id ?? null);

  const isAuth = isAuthError(meQ.error ?? configQ.error ?? permsQ.error);

  // 401/403 → purge tokens + AuthenticatedLayout redirige vers /login
  useEffect(() => {
    if (isAuth) {
      storage.removeItem('tokens');
      clearTokens(queryClient);
    }
  }, [isAuth]);

  if (isAuth) return null;
  if (!claims) return <>{error}</>;

  // Les routes back peuvent encore loading — on n'attend pas, on initialise
  // depuis le JWT et on merge avec les valeurs back dès qu'elles arrivent.

  const me: ExtranetMeResponse = meQ.data ?? buildMe(claims);
  const config: AccountConfig = configQ.data ?? buildConfig(claims);
  const basePermissions: UserPermissionsResponse = permsQ.data ?? buildPermissions(claims);

  // Override Admin si EXTRANET_ADMIN remonté par /v1/users/:id/permissions.
  const isExtranetAdmin = Boolean(rolesPermsQ.data?.permissions?.includes('EXTRANET_ADMIN'));
  const permissions: UserPermissionsResponse = isExtranetAdmin
    ? {
        ...basePermissions,
        modules: {
          ...basePermissions.modules,
          admin: { read: true, manageUsers: true },
        },
      }
    : basePermissions;

  const value: AccountContextValue = {
    me,
    config,
    permissions,
    can: (module, flag) => {
      const modulePerms = permissions.modules[module] as Record<string, boolean>;
      return Boolean(modulePerms[flag as string]);
    },
  };

  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
};

export const useAccount = (): AccountContextValue => {
  const ctx = useContext(AccountContext);
  if (!ctx) {
    throw new Error("useAccount doit être utilisé à l'intérieur d'<AccountProvider>");
  }
  return ctx;
};
